import L from 'leaflet';
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import Icon, { type IconName } from '@/components/ui/Icon/Icon';
import {
  DOT_SIZE,
  LABEL_BORDER,
  LABEL_FONT_PX,
  LABEL_FONT_WEIGHT,
  LABEL_HEIGHT,
  LABEL_PAD_X,
  LABEL_PAD_Y,
  LABEL_RADIUS,
  LABEL_TAIL,
  MARKER_SIZE,
} from '@/lib/map-labels';
import type { LabelSide } from '@/lib/map-spec';

// Vite bundling breaks the default marker icon URL resolution.
// Reassign so <Marker> renders correctly.
type IconProtoWithUrl = L.Icon.Default & { _getIconUrl?: unknown };
delete (L.Icon.Default.prototype as IconProtoWithUrl)._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// 調査地点（buyer が入力・選択した中心点）のマーカー。
// 墨色だと地図の地名・罫線と同化して埋もれるため、ブランド四色のうち最も視認性が高く
// 「印」を担う朱を充てる。雲色のリング＋墨色の極細アウトライン＋影で、明るいタイルでも
// 暗い地物の上でも輪郭が立つようにしている。見た目は leaflet-overrides.css 側で定義。
export const ienoneMarkerIcon = L.divIcon({
  className: 'ienone-marker',
  html: '<span class="ienone-marker__pin"></span>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// 入力直後の地図（SiteMap）用。上記に脈打つ波紋を重ね、いま置いた一点へ視線を誘導する。
export const ienoneMarkerActiveIcon = L.divIcon({
  className: 'ienone-marker ienone-marker--active',
  html: '<span class="ienone-marker__pulse"></span><span class="ienone-marker__pin"></span>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
}

// 吹き出しの本体を side の側へ置く CSS (マーカー要素の左上 = 点。幅は文字に合わせて伸びる)。
const BALLOON_BODY_POS: Record<LabelSide, string> = {
  above: `left:0;top:${-LABEL_TAIL}px;transform:translate(-50%,-100%)`,
  below: `left:0;top:${LABEL_TAIL}px;transform:translate(-50%,0)`,
  right: `left:${LABEL_TAIL}px;top:0;transform:translate(0,-50%)`,
  left: `left:${-LABEL_TAIL}px;top:0;transform:translate(-100%,-50%)`,
};
// しっぽ = 45° 回した正方形 (対角線の半分 = LABEL_TAIL)。中心を本体の点側の辺の中央 (点から LABEL_TAIL) に置き、
// 回転後に点を向く 2 辺だけ雲色の縁を付ける。本体の後に描いて本体の縁を隠す。
const BALLOON_TAIL: Record<LabelSide, { x: number; y: number; edges: [string, string] }> = {
  above: { x: 0, y: -LABEL_TAIL, edges: ['right', 'bottom'] },
  below: { x: 0, y: LABEL_TAIL, edges: ['top', 'left'] },
  right: { x: LABEL_TAIL, y: 0, edges: ['left', 'bottom'] },
  left: { x: -LABEL_TAIL, y: 0, edges: ['top', 'right'] },
};

// 価格の吹き出し (MarkerSpec.label)。点の側 (labelSide) に単価の色で塗った小札を出し、45° のしっぽの先端を
// 点の座標に置く。寸法は lib/map-labels.ts が定義し、PDF (pdf/render-map.ts) も同じ寸法で描く。
// iconSize は 0 にして点をマーカー要素の左上に置き、子要素を絶対配置で側へ寄せる。
export function priceBalloonIcon(label: string, color: string, side: LabelSide = 'above'): L.DivIcon {
  const tailSquare = Math.round(LABEL_TAIL * Math.SQRT2);
  const tail = BALLOON_TAIL[side];
  const body =
    `<span style="position:absolute;${BALLOON_BODY_POS[side]};white-space:nowrap;` +
    `box-sizing:border-box;height:${LABEL_HEIGHT}px;font-family:var(--font-body);font-size:${LABEL_FONT_PX}px;` +
    `font-weight:${LABEL_FONT_WEIGHT};line-height:1;color:var(--color-sumi);background:${color};` +
    `border:${LABEL_BORDER}px solid var(--color-kumo);border-radius:${LABEL_RADIUS}px;` +
    `padding:${LABEL_PAD_Y}px ${LABEL_PAD_X}px;">${escapeHtml(label)}</span>`;
  const tailHtml =
    `<span style="position:absolute;left:${tail.x}px;top:${tail.y}px;` +
    `width:${tailSquare}px;height:${tailSquare}px;box-sizing:border-box;background:${color};` +
    `border:0 solid var(--color-kumo);border-${tail.edges[0]}-width:${LABEL_BORDER}px;` +
    `border-${tail.edges[1]}-width:${LABEL_BORDER}px;transform:translate(-50%,-50%) rotate(45deg);"></span>`;
  return L.divIcon({
    className: 'amenity-marker',
    html: body + tailHtml,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    // ツールチップ (direction: top) が吹き出しに被らないよう、本体の上端を基準にする。
    tooltipAnchor: [0, side === 'above' ? -(LABEL_TAIL + LABEL_HEIGHT) : side === 'below' ? 0 : -LABEL_HEIGHT / 2],
  });
}

// 吹き出しを落とした価格点の小さな丸 (MarkerSpec に icon も label も無い点)。色とツールチップだけ残す。
export function dotMarkerIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: 'amenity-marker',
    html:
      `<span style="display:block;width:${DOT_SIZE}px;height:${DOT_SIZE}px;box-sizing:border-box;border-radius:50%;` +
      `background:${color};border:${LABEL_BORDER}px solid var(--color-kumo);"></span>`,
    iconSize: [DOT_SIZE, DOT_SIZE],
    iconAnchor: [DOT_SIZE / 2, DOT_SIZE / 2],
  });
}

// 施設・ハザード用マーカー（アイコン版）。雲色の Tabler outline アイコンを
// カテゴリ色の円に据える。絵文字の代替（§5 アイコノグラフィ / §8 アンチパターン）。
export function amenityIconMarker(name: IconName, color: string): L.DivIcon {
  // アイコンは currentColor 継承にし、span の color(雲)で着色（hex リテラルを使わずトークン参照）。
  const svg = renderToStaticMarkup(
    createElement(Icon, { name, size: 16, strokeWidth: 2 }),
  );
  return L.divIcon({
    className: 'amenity-marker',
    html: `<span style="display:flex;align-items:center;justify-content:center;width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;border-radius:50%;color:var(--color-kumo);background:${color};border:2px solid var(--color-kumo);">${svg}</span>`,
    iconSize: [MARKER_SIZE, MARKER_SIZE],
    iconAnchor: [MARKER_SIZE / 2, MARKER_SIZE / 2],
  });
}
